import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { EASE, Reveal } from "./motion-primitives";

// Featured/related destination card that links to the detail page.
export const DestinationTile = ({ d, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-8% 0px" }}
    transition={{ duration: 0.7, ease: EASE }}
  >
    <Link
      to={`/destinations/${d.slug}`}
      data-testid={testId || `destination-tile-${d.slug}`}
      className="group flex flex-col text-left"
    >
      <div className="relative aspect-[4/5] w-full overflow-hidden">
        <img
          src={d.image}
          alt={`${d.name}, ${d.country}`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-ink/10 to-transparent" />
        <span className="absolute left-4 top-4 font-sans text-[0.6rem] uppercase tracking-[0.24em] text-cream/80">
          {d.region}
        </span>
        <span className="absolute right-4 top-4 text-cream/70 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
          <ArrowUpRight size={18} strokeWidth={1.5} />
        </span>
      </div>
      <div className="pt-5">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-serif text-2xl font-light tracking-tight text-cream">{d.name}</h3>
          <span className="font-sans text-xs uppercase tracking-[0.2em] text-sand">{d.country}</span>
        </div>
        <p className="mt-3 font-serif text-base font-light italic leading-snug text-cream/80">
          “{d.shortDescription}”
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-x-2 font-sans text-[0.7rem] uppercase tracking-[0.16em] text-sand">
          <span className="text-teal">{d.tags.join(" · ")}</span>
        </div>
        <p className="mt-2 font-sans text-xs text-sand">Best for: {d.bestFor.join(" · ")}</p>
      </div>
    </Link>
  </motion.div>
);

// Region browse card.
export const RegionCard = ({ region, image, count, active, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(region)}
    data-testid={`region-card-${region.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
    className={`group relative flex min-h-[220px] overflow-hidden text-left transition-all duration-500 ${
      active ? "ring-1 ring-teal" : ""
    }`}
  >
    <img
      src={image}
      alt={region}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/10 transition-colors duration-500 group-hover:from-ink/90" />
    <div className="relative z-10 mt-auto p-6">
      <h3 className="font-serif text-2xl font-light tracking-tight text-cream">{region}</h3>
      <p className="mt-1 font-sans text-xs uppercase tracking-[0.2em] text-sand">
        {count} {count === 1 ? "destination" : "destinations"}
      </p>
    </div>
  </button>
);

// Detail-page section wrapper with an editorial H2 + overline.
export const DetailSection = ({ id, overline, title, children, className = "" }) => (
  <section id={id} className={`border-t border-charcoal py-16 lg:py-24 ${className}`}>
    <div className="mx-auto max-w-[1400px] px-6 sm:px-10 lg:px-16">
      {(overline || title) && (
        <Reveal>
          <div className="mb-10 lg:mb-14">
            {overline && (
              <div className="mb-5 flex items-center gap-4">
                <span className="h-px w-10 bg-teal" />
                <span className="font-sans text-xs uppercase tracking-[0.3em] text-teal">
                  {overline}
                </span>
              </div>
            )}
            {title && (
              <h2 className="max-w-3xl font-serif text-3xl font-light leading-[1.05] tracking-tight text-cream sm:text-4xl lg:text-5xl">
                {title}
              </h2>
            )}
          </div>
        </Reveal>
      )}
      {children}
    </div>
  </section>
);
